/**
 * Brand Brain — typed schema for the ONE source of truth on brand voice.
 *
 * The Brand Brain reconciles the three fragmented voice locations
 * (brain/companyBrain facts, docs/brandDoc render constants, ad-hoc prompt
 * strings) into a single, typed, scope-routed collection of entries. Every
 * downstream agentic output composes its brand fragment from this substrate
 * via `composeBrandPrompt` (see ./compose.ts) — never from scattered strings.
 *
 * Design mirrors the existing record patterns in src/lib/db/types.ts:
 * string ids, ISO-8601 timestamps, JSON-safe values only. Deliberately PURE:
 * no store import, no firebase, no clock beyond the default-filler — the
 * projection/query layer lives in ./store.ts and persistence rides the
 * shipped `db` store unchanged (its Store interface is untouched).
 */

/** What kind of brand knowledge an entry carries. */
export type BrandEntryKind =
  | 'voice'
  | 'positioning'
  | 'offer'
  | 'directive'
  | 'lexicon'
  | 'proof';

/** Which output surface an entry applies to. 'all' matches every scope. */
export type BrandScope = 'content' | 'proposal' | 'blog' | 'email' | 'all';

export type BrandStatus = 'active' | 'draft' | 'archived';

/** One atomic, scope-routed brand fact / rule / phrase. */
export interface BrandEntry {
  /** Stable opaque id — becomes the persisted doc id. */
  id: string;
  kind: BrandEntryKind;
  /** Short human label, e.g. "No exclamation marks". */
  title: string;
  /** The full text injected into prompts. */
  body: string;
  /** Higher weight wins the token budget first. */
  weight: number;
  /** Output surfaces this entry routes to ('all' = every surface). */
  scopes: BrandScope[];
  status: BrandStatus;
  /** Who last touched it — operator name or agent id. */
  updatedBy: string;
  /** ISO-8601 UTC. */
  updatedAt: string;
}

/** The whole Brand Brain: a flat, filterable list of entries. */
export interface BrandBrain {
  entries: BrandEntry[];
}

/** Honest empty state — new tenants start with zero brand claims. */
export const emptyBrandBrain: BrandBrain = { entries: [] };

// ---------------------------------------------------------------------------
// Defaults filler
// ---------------------------------------------------------------------------

/** Id generator: crypto.randomUUID when available, else a
 *  timestamp+counter fallback (still unique within a session). */
let fallbackCounter = 0;
function makeId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  fallbackCounter += 1;
  return `be-${Date.now().toString(36)}-${fallbackCounter}`;
}

/**
 * Fill a partial entry into a complete BrandEntry. Callers that care about
 * determinism (tests, seeders) pass explicit `id`/`updatedAt`; UI callers
 * let the filler stamp them. Never mutates the input.
 */
export function makeBrandEntry(partial: Partial<BrandEntry>): BrandEntry {
  return {
    id: partial.id ?? makeId(),
    kind: partial.kind ?? 'voice',
    title: partial.title ?? '',
    body: partial.body ?? '',
    weight: partial.weight ?? 1,
    scopes: partial.scopes ? [...partial.scopes] : ['all'],
    status: partial.status ?? 'active',
    updatedBy: partial.updatedBy ?? 'operator',
    updatedAt: partial.updatedAt ?? new Date().toISOString(),
  };
}
